import React from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Image,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  PanResponder,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";
import AssignImageToPatientModal from "../../../uncommon/native/modals/AssignImageToPatientModal";
import {
  assignPatientToClinicImage,
  getPatientImages,
  TGetPatientImageData,
} from "../../../../services/api";
import { logUIError } from "../../../../services/api-misc";
import { getTransforms } from "../../../../services/api-clinic";
import { TPatientInfo } from "../../../../services/api-clinic";
import { capitalizeString, createUUID } from "../../../../utils/functions";
import { mixpanel } from "../../../../utils/mixpanel";
import { TSession } from "../../../../utils/types";
import {
  simulateTabBodyProcedure,
  simulateTabBreastProcedure,
  simulateTabFaceProcedure,
  simulateTabMommyMakeoverProcedure,
} from "./simulateTabConstants";

const TRANSFORMS_PAGE_SIZE = 100;
const TRANSFORM_SOURCES = ["dashboard", "ios"] as const;
const MIN_PAST_SIMULATION_DATE = new Date(2026, 1, 19);

export type TPastSimulationSelection = {
  imageId: string;
  frameUrls: string[];
  procedureTypes: string[];
};

type PastSimulationsSectionProps = {
  session: TSession;
  patientId?: string;
  refreshTrigger?: string[] | null;
  onSelectPastSimulation?: (selection: TPastSimulationSelection) => void;
};

type TPastSimulationItem = {
  id: string | number;
  patient_name?: string | null;
  download_url: string;
  download_urls?: string[];
  procedure_types?: string[];
  metadata?: {
    procedure_types?: string[];
    [key: string]: unknown;
  };
  image_data?: {
    procedure_types?: string[];
    [key: string]: unknown;
  };
  upload_type?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

type GroupedPastTransforms = {
  key: string;
  label: string;
  items: TPastSimulationItem[];
};

const PastSimulationsSection: React.FC<PastSimulationsSectionProps> = ({
  session,
  patientId,
  refreshTrigger,
  onSelectPastSimulation,
}) => {
  const groupedCarouselRef =
    React.useRef<FlatList<GroupedPastTransforms>>(null);

  const parseUtcToLocalDate = React.useCallback(
    (value: string): Date | null => {
      if (!value) return null;

      const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(value);
      const utcValue = hasTimezone ? value : `${value}Z`;
      const parsed = new Date(utcValue);

      if (Number.isNaN(parsed.getTime())) return null;
      return parsed;
    },
    [],
  );

  const [pastTransforms, setPastTransforms] = React.useState<
    TPastSimulationItem[]
  >([]);
  const [isLoadingPastTransforms, setIsLoadingPastTransforms] =
    React.useState<boolean>(false);
  const [isLoadingMorePastTransforms, setIsLoadingMorePastTransforms] =
    React.useState<boolean>(false);
  const [hasMorePastTransforms, setHasMorePastTransforms] =
    React.useState<boolean>(true);
  const [excludedProcedureFilters, setExcludedProcedureFilters] =
    React.useState<string[]>([]);
  const [carouselViewportWidth, setCarouselViewportWidth] =
    React.useState<number>(0);
  const [carouselContentWidth, setCarouselContentWidth] =
    React.useState<number>(0);
  const [carouselScrollX, setCarouselScrollX] = React.useState<number>(0);
  const [scrubTrackWidth, setScrubTrackWidth] = React.useState<number>(0);
  const [assignModalVisible, setAssignModalVisible] =
    React.useState<boolean>(false);
  const [assignTargetTransform, setAssignTargetTransform] =
    React.useState<TPastSimulationItem | null>(null);
  const transformsCursorRef = React.useRef<string | null>(null);

  const isRenderableImageUrl = React.useCallback((url: string) => {
    return new Promise<boolean>((resolve) => {
      let done = false;
      const finish = (result: boolean) => {
        if (done) return;
        done = true;
        resolve(result);
      };

      const timeout = setTimeout(() => finish(false), 5000);

      Image.getSize(
        url,
        () => {
          clearTimeout(timeout);
          finish(true);
        },
        () => {
          clearTimeout(timeout);
          finish(false);
        },
      );
    });
  }, []);

  const filterRenderableTransforms = React.useCallback(
    async (items: TPastSimulationItem[]) => {
      if (items.length === 0) return items;

      const accessibility = await Promise.all(
        items.map((item) => isRenderableImageUrl(item.download_url)),
      );

      return items.filter((_, index) => accessibility[index]);
    },
    [isRenderableImageUrl],
  );

  const mapPatientImageToPastSimulation = React.useCallback(
    (item: TGetPatientImageData): TPastSimulationItem | null => {
      const downloadUrl =
        item.thumbnail_presigned_urls?.[
          item.thumbnail_presigned_urls.length - 1
        ] ||
        item.presigned_urls?.[item.presigned_urls.length - 1] ||
        item.presigned_urls?.[0];

      if (!downloadUrl) return null;

      return {
        id: String(item.image_data.id),
        download_url: downloadUrl,
        // Full frame set (preop -> frames -> postop) for loading into the main viewer.
        download_urls: item.presigned_urls,
        procedure_types: item.image_data.procedure_types,
        image_data: {
          procedure_types: item.image_data.procedure_types,
          created_at: item.image_data.created_at,
          updated_at: item.image_data.updated_at,
        },
        upload_type: item.image_data.upload_type,
        created_at: item.image_data.created_at,
        updated_at: item.image_data.updated_at,
      };
    },
    [],
  );

  const fetchTransforms = React.useCallback(
    async (replace: boolean = false) => {
      if (!session?.accessToken || !session?.clinicId) return;
      if (replace) setIsLoadingPastTransforms(true);
      else setIsLoadingMorePastTransforms(true);

      try {
        if (patientId) {
          const patientImages = await getPatientImages(session, patientId, {
            uploadTypes: ["TransformImageForUser", "TransformImageForForm"],
          });
          const mappedTransforms = patientImages
            .map(mapPatientImageToPastSimulation)
            .filter((item): item is TPastSimulationItem => Boolean(item));
          const renderableIncoming =
            await filterRenderableTransforms(mappedTransforms);
          setPastTransforms(renderableIncoming);
          setHasMorePastTransforms(false);
          return;
        }

        if (replace) {
          transformsCursorRef.current = null;
        }

        const response = await getTransforms(session, {
          sources: [...TRANSFORM_SOURCES],
          limit: TRANSFORMS_PAGE_SIZE,
          cursor: transformsCursorRef.current,
        });
        transformsCursorRef.current = response.next_cursor;

        const incoming = (response.results ?? []).filter(
          (item) =>
            typeof item.download_url === "string" &&
            item.download_url.length > 0,
        );
        const renderableIncoming = await filterRenderableTransforms(incoming);
        setPastTransforms((prev) => {
          const merged = replace
            ? renderableIncoming
            : [...prev, ...renderableIncoming];
          const deduped = new Map<string, TPastSimulationItem>();
          for (const item of merged) deduped.set(String(item.id), item);
          return Array.from(deduped.values());
        });
        setHasMorePastTransforms(response.has_more);
      } catch (error) {
        console.error("Error fetching transforms:", error);
        logUIError({
          message: "Failed to fetch transforms",
          page_url:
            typeof window !== "undefined" ? window.location.href : undefined,
          user_email: session?.userEmail || undefined,
          clinic_name: session?.clinicName || undefined,
          clinic_id: session?.clinicId || undefined,
          context: {
            component: "PastSimulationsSection",
            patientId,
            cursor: transformsCursorRef.current,
            replace,
            error: error instanceof Error ? error.message : String(error),
          },
        }).catch((logErr) =>
          console.warn("logUIError failed in PastSimulationsSection", logErr),
        );
      } finally {
        if (replace) setIsLoadingPastTransforms(false);
        else setIsLoadingMorePastTransforms(false);
      }
    },
    [
      filterRenderableTransforms,
      mapPatientImageToPastSimulation,
      patientId,
      session,
    ],
  );

  React.useEffect(() => {
    fetchTransforms(true);
  }, [fetchTransforms]);

  React.useEffect(() => {
    if (refreshTrigger && refreshTrigger.length > 0) {
      fetchTransforms(true);
    }
  }, [refreshTrigger, fetchTransforms]);

  const getProcedureTypesFromTransform = React.useCallback(
    (transform: TPastSimulationItem): string[] => {
      const rawCandidates: unknown[] = [
        transform?.procedure_types,
        transform?.metadata?.procedure_types,
        transform?.image_data?.procedure_types,
      ];

      const merged = rawCandidates
        .flatMap((candidate) => (Array.isArray(candidate) ? candidate : []))
        .filter((value): value is string => typeof value === "string");

      return Array.from(new Set(merged));
    },
    [],
  );

  const getTransformDateValue = React.useCallback(
    (transform: TPastSimulationItem): Date | null => {
      const candidates: unknown[] = [
        transform.date_taken,
        transform.taken_at,
        transform.captured_at,
        transform.created_at,
        transform.updated_at,
        transform?.metadata?.date_taken,
        transform?.metadata?.taken_at,
        transform?.metadata?.captured_at,
        transform?.metadata?.created_at,
      ];

      for (const candidate of candidates) {
        if (typeof candidate !== "string" || candidate.length === 0) continue;
        const parsed = parseUtcToLocalDate(candidate);
        if (parsed) return parsed;
      }

      return null;
    },
    [parseUtcToLocalDate],
  );

  const eligiblePastTransforms = React.useMemo(() => {
    return pastTransforms.filter((transform) => {
      const date = getTransformDateValue(transform);
      return date ? date >= MIN_PAST_SIMULATION_DATE : false;
    });
  }, [getTransformDateValue, pastTransforms]);

  const procedureFilterOptions = React.useMemo(() => {
    const inUseProcedures = Array.from(
      new Set(
        eligiblePastTransforms.flatMap((transform) =>
          getProcedureTypesFromTransform(transform),
        ),
      ),
    );

    const preferredOrder = [
      ...simulateTabBreastProcedure,
      ...simulateTabBodyProcedure,
      ...simulateTabFaceProcedure,
      ...simulateTabMommyMakeoverProcedure,
    ];
    const preferredRank = new Map<string, number>(
      preferredOrder.map((procedure, index) => [procedure, index]),
    );

    return inUseProcedures
      .map((value) => ({ value, label: capitalizeString(value) }))
      .sort((a, b) => {
        const rankA = preferredRank.get(a.value);
        const rankB = preferredRank.get(b.value);

        if (rankA !== undefined && rankB !== undefined) return rankA - rankB;
        if (rankA !== undefined) return -1;
        if (rankB !== undefined) return 1;
        return a.label.localeCompare(b.label);
      });
  }, [eligiblePastTransforms, getProcedureTypesFromTransform]);

  const filteredPastTransforms = React.useMemo(() => {
    if (excludedProcedureFilters.length === 0) return eligiblePastTransforms;

    const excludedSet = new Set(excludedProcedureFilters);
    return eligiblePastTransforms.filter((transform) =>
      getProcedureTypesFromTransform(transform).some(
        (procedure) => !excludedSet.has(procedure),
      ),
    );
  }, [
    eligiblePastTransforms,
    excludedProcedureFilters,
    getProcedureTypesFromTransform,
  ]);

  const groupedPastTransforms = React.useMemo(() => {
    const formatter = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const groupedMap = new Map<
      string,
      { label: string; sortValue: number; items: TPastSimulationItem[] }
    >();

    for (const transform of filteredPastTransforms) {
      const date = getTransformDateValue(transform);
      const key = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0",
          )}-${String(date.getDate()).padStart(2, "0")}`
        : "unknown-date";
      const label = date ? formatter.format(date) : "Unknown Date";
      const sortValue = date
        ? new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
          ).getTime()
        : 0;

      if (!groupedMap.has(key)) {
        groupedMap.set(key, { label, sortValue, items: [] });
      }
      groupedMap.get(key)?.items.push(transform);
    }

    return Array.from(groupedMap.entries())
      .sort((a, b) => b[1].sortValue - a[1].sortValue)
      .map(([key, value]) => ({
        key,
        label: value.label,
        items: value.items,
      }));
  }, [filteredPastTransforms, getTransformDateValue]);

  const maxCarouselScroll = React.useMemo(
    () => Math.max(carouselContentWidth - carouselViewportWidth, 0),
    [carouselContentWidth, carouselViewportWidth],
  );
  const scrubThumbWidth = React.useMemo(() => {
    if (scrubTrackWidth <= 0 || carouselContentWidth <= 0) return 0;
    const proportionalWidth =
      (carouselViewportWidth / carouselContentWidth) * scrubTrackWidth;
    return Math.max(36, Math.min(scrubTrackWidth, proportionalWidth));
  }, [carouselContentWidth, carouselViewportWidth, scrubTrackWidth]);
  const maxScrubTravel = React.useMemo(
    () => Math.max(scrubTrackWidth - scrubThumbWidth, 0),
    [scrubThumbWidth, scrubTrackWidth],
  );
  const scrubThumbOffset = React.useMemo(() => {
    if (maxCarouselScroll <= 0 || maxScrubTravel <= 0) return 0;
    return (carouselScrollX / maxCarouselScroll) * maxScrubTravel;
  }, [carouselScrollX, maxCarouselScroll, maxScrubTravel]);

  React.useEffect(() => {
    setCarouselScrollX((previous) =>
      Math.min(
        previous,
        Math.max(carouselContentWidth - carouselViewportWidth, 0),
      ),
    );
  }, [carouselContentWidth, carouselViewportWidth]);

  const handleCarouselScroll = React.useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      setCarouselScrollX(event.nativeEvent.contentOffset.x);
    },
    [],
  );

  const handleCarouselLayout = React.useCallback((event: LayoutChangeEvent) => {
    setCarouselViewportWidth(event.nativeEvent.layout.width);
  }, []);

  const handleCarouselContentSizeChange = React.useCallback((width: number) => {
    setCarouselContentWidth(width);
  }, []);

  const scrollCarouselTo = React.useCallback(
    (targetOffset: number) => {
      const clampedOffset = Math.max(
        0,
        Math.min(targetOffset, maxCarouselScroll),
      );
      groupedCarouselRef.current?.scrollToOffset({
        offset: clampedOffset,
        animated: false,
      });
      setCarouselScrollX(clampedOffset);
    },
    [maxCarouselScroll],
  );

  const updateCarouselFromScrubPosition = React.useCallback(
    (locationX: number) => {
      if (maxScrubTravel <= 0 || maxCarouselScroll <= 0) return;
      const clampedX = Math.max(
        0,
        Math.min(locationX - scrubThumbWidth / 2, maxScrubTravel),
      );
      const nextScroll = (clampedX / maxScrubTravel) * maxCarouselScroll;
      scrollCarouselTo(nextScroll);
    },
    [maxCarouselScroll, maxScrubTravel, scrubThumbWidth, scrollCarouselTo],
  );

  const scrubPanResponder = React.useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderGrant: (event) => {
          updateCarouselFromScrubPosition(event.nativeEvent.locationX);
        },
        onPanResponderMove: (event) => {
          updateCarouselFromScrubPosition(event.nativeEvent.locationX);
        },
      }),
    [updateCarouselFromScrubPosition],
  );

  const toggleProcedureFilter = React.useCallback((procedureValue: string) => {
    setExcludedProcedureFilters((previous) =>
      previous.includes(procedureValue)
        ? previous.filter((value) => value !== procedureValue)
        : [...previous, procedureValue],
    );
  }, []);

  const handleLoadMorePastTransforms = React.useCallback(() => {
    if (
      isLoadingPastTransforms ||
      isLoadingMorePastTransforms ||
      !hasMorePastTransforms ||
      Boolean(patientId)
    ) {
      return;
    }
    fetchTransforms(false);
  }, [
    fetchTransforms,
    hasMorePastTransforms,
    isLoadingMorePastTransforms,
    isLoadingPastTransforms,
  ]);

  const handleSelectPastSimulation = React.useCallback(
    (transform: TPastSimulationItem) => {
      // Prefer the full frame set (preop -> frames -> postop); fall back to the
      // single postop image when the frame set is unavailable.
      const frameUrls =
        transform.download_urls && transform.download_urls.length > 0
          ? transform.download_urls
          : [transform.download_url];

      onSelectPastSimulation?.({
        imageId: String(transform.id),
        frameUrls,
        procedureTypes: getProcedureTypesFromTransform(transform),
      });
    },
    [getProcedureTypesFromTransform, onSelectPastSimulation],
  );

  const handleDownloadTransform = React.useCallback(
    async (transform: TPastSimulationItem) => {
      if (!transform.download_url) return;
      try {
        const response = await fetch(transform.download_url);
        if (!response.ok) throw new Error("Network response was not ok");
        const blob = await response.blob();
        const link = document.createElement("a");
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.setAttribute("download", `${createUUID()}.png`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        Alert.alert("Download failed", "Failed to download image.");
      }
    },
    [],
  );

  const openAssignModal = React.useCallback(
    (transform: TPastSimulationItem) => {
      setAssignTargetTransform(transform);
      setAssignModalVisible(true);
    },
    [],
  );

  const handleAssignToPatient = React.useCallback(
    async (patient: TPatientInfo) => {
      if (!assignTargetTransform) return;
      const imageId = Number(assignTargetTransform.id);
      if (!Number.isFinite(imageId)) {
        Alert.alert(
          "Cannot assign image",
          "This simulation is missing a valid image id.",
        );
        return;
      }

      try {
        mixpanel.track("Assign Image To Patient Triggered", {
          "Clinic Name": session?.clinicName ?? "clinic not found",
          Flow: "PastSimulations",
          Endpoint:
            "/clinics/v1/clinic/{clinic_id}/images/{image_id}/assign-patient",
          "Image ID": imageId,
          "Patient UUID": patient.uuid,
        });
        await assignPatientToClinicImage(session, imageId, patient.uuid);
        Alert.alert("Success", "Image assigned to patient.");
        fetchTransforms(true);
      } catch (error) {
        Alert.alert(
          "Assign failed",
          "Failed to assign image. Please try again.",
        );
        throw error;
      }
    },
    [assignTargetTransform, fetchTransforms, session],
  );

  const renderPastSimulationThumb = ({
    item,
  }: {
    item: TPastSimulationItem;
  }) => {
    if (!item.download_url) return null;
    const patientName = item.patient_name?.trim() || null;

    return (
      <View style={styles.carouselThumb}>
        <View style={styles.carouselItem}>
          <TouchableOpacity
            style={styles.carouselImagePressable}
            onPress={() => handleSelectPastSimulation(item)}
            activeOpacity={0.9}
            accessibilityRole="button"
            accessibilityLabel={
              patientName
                ? `Open simulation for ${patientName}`
                : "Open unassigned simulation"
            }
          >
            <Image
              source={{ uri: item.download_url }}
              style={styles.carouselImage}
            />
          </TouchableOpacity>
          <View style={styles.imageActionRow}>
            <TouchableOpacity
              onPress={(event: any) => {
                event?.stopPropagation?.();
                handleDownloadTransform(item);
              }}
              style={styles.imageActionButton}
              accessibilityRole="button"
              accessibilityLabel={
                patientName
                  ? `Download simulation for ${patientName}`
                  : "Download unassigned simulation"
              }
            >
              <Ionicons
                name="download-outline"
                size={16}
                color="#FFFFFF"
                style={styles.actionIconShadow}
              />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={(event: any) => {
                event?.stopPropagation?.();
                openAssignModal(item);
              }}
              style={styles.imageActionButton}
              accessibilityRole="button"
              accessibilityLabel={
                patientName
                  ? `Change patient assignment for ${patientName}'s simulation`
                  : "Assign simulation to a patient"
              }
            >
              <Ionicons
                name="person-add-outline"
                size={16}
                color="#FFFFFF"
                style={styles.actionIconShadow}
              />
            </TouchableOpacity>
          </View>
        </View>
        {patientName ? (
          <Text
            style={styles.patientName}
            numberOfLines={1}
            ellipsizeMode="tail"
            accessibilityLabel={patientName}
          >
            {patientName}
          </Text>
        ) : null}
      </View>
    );
  };

  const renderGroupedCarouselCard = ({
    item,
  }: {
    item: GroupedPastTransforms;
  }) => (
    <View style={styles.groupSectionCard}>
      <Text style={styles.groupDateText}>{item.label}</Text>
      <View style={styles.groupCarouselImagesRow}>
        {item.items.map((transform) => (
          <View key={`carousel-${item.key}-${transform.id}`}>
            {renderPastSimulationThumb({ item: transform })}
          </View>
        ))}
      </View>
    </View>
  );

  return (
    <View style={styles.pastSimulationsSection}>
      <View style={styles.pastSimulationsHeaderRow}>
        <Text style={styles.pastSimulationsTitle}>Past Simulations</Text>
      </View>

      {procedureFilterOptions.length > 0 ? (
        <View style={styles.filtersWrap}>
          {procedureFilterOptions.map((filterOption) => {
            const isSelected = !excludedProcedureFilters.includes(
              filterOption.value,
            );

            return (
              <Pressable
                key={filterOption.value}
                style={styles.filterItem}
                onPress={() => toggleProcedureFilter(filterOption.value)}
              >
                <View
                  style={[
                    styles.checkboxBox,
                    isSelected ? styles.checkboxBoxSelected : null,
                  ]}
                >
                  {isSelected ? (
                    <Ionicons name="checkmark" size={12} color="#fff" />
                  ) : null}
                </View>
                <Text style={styles.filterLabel}>{filterOption.label}</Text>
              </Pressable>
            );
          })}
        </View>
      ) : null}

      {isLoadingPastTransforms && eligiblePastTransforms.length === 0 ? (
        <View style={styles.pastSimulationsLoadingWrap}>
          <ActivityIndicator color="#00B7F4" />
        </View>
      ) : null}

      {!isLoadingPastTransforms && filteredPastTransforms.length === 0 ? (
        <Text style={styles.emptyPastSimulationsText}>
          {eligiblePastTransforms.length === 0
            ? "No past simulations yet."
            : "No simulations match the checked procedures."}
        </Text>
      ) : null}

      {groupedPastTransforms.length > 0 ? (
        <FlatList
          ref={groupedCarouselRef}
          horizontal
          data={groupedPastTransforms}
          keyExtractor={(group) => group.key}
          renderItem={renderGroupedCarouselCard}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.groupedCarouselContent}
          onScroll={handleCarouselScroll}
          scrollEventThrottle={16}
          onLayout={handleCarouselLayout}
          onContentSizeChange={handleCarouselContentSizeChange}
          directionalLockEnabled
          nestedScrollEnabled
          onEndReachedThreshold={0.4}
          onEndReached={handleLoadMorePastTransforms}
        />
      ) : null}

      {groupedPastTransforms.length > 0 && maxCarouselScroll > 0 ? (
        <View
          style={styles.scrubTrack}
          onLayout={(event) =>
            setScrubTrackWidth(event.nativeEvent.layout.width)
          }
          {...scrubPanResponder.panHandlers}
        >
          <View
            style={[
              styles.scrubThumb,
              {
                width: scrubThumbWidth,
                transform: [{ translateX: scrubThumbOffset }],
              },
            ]}
          />
        </View>
      ) : null}

      {isLoadingMorePastTransforms ? (
        <View style={styles.pastSimulationsLoadMoreWrap}>
          <ActivityIndicator color="#00B7F4" />
        </View>
      ) : null}

      <AssignImageToPatientModal
        visible={assignModalVisible}
        onClose={() => setAssignModalVisible(false)}
        onAssign={handleAssignToPatient}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  pastSimulationsSection: {
    marginTop: 20,
    marginBottom: 20,
  },
  pastSimulationsHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  pastSimulationsTitle: {
    fontFamily: "Urbanist700",
    fontSize: 18,
    color: "#121212",
  },
  pastSimulationsLoadingWrap: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 20,
  },
  pastSimulationsLoadMoreWrap: {
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 10,
  },
  emptyPastSimulationsText: {
    fontFamily: "Urbanist500",
    fontSize: 14,
    color: "#666",
    paddingVertical: 12,
  },
  filtersWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 12,
  },
  filterItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  checkboxBox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#9AA3B2",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
  },
  checkboxBoxSelected: {
    borderColor: "#00B7F4",
    backgroundColor: "#00B7F4",
  },
  filterLabel: {
    fontFamily: "Urbanist500",
    fontSize: 13,
    color: "#212121",
  },
  groupedCarouselContent: {
    gap: 12,
    paddingVertical: 4,
  },
  scrubTrack: {
    height: 8,
    borderRadius: 999,
    backgroundColor: "#DEE4EC",
    marginTop: 4,
    marginBottom: 4,
    width: "100%",
    overflow: "hidden",
    justifyContent: "center",
  },
  scrubThumb: {
    height: "100%",
    borderRadius: 999,
    backgroundColor: "#00B7F4",
  },
  groupSectionCard: {
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#DDE3EA",
    borderRadius: 12,
    backgroundColor: "#FAFCFE",
    padding: 10,
  },
  groupCarouselImagesRow: {
    flexDirection: "row",
    gap: 12,
    alignItems: "flex-start",
  },
  groupDateText: {
    fontFamily: "Urbanist600",
    fontSize: 14,
    color: "#4A5568",
    marginBottom: 8,
  },
  carouselItem: {
    width: 110,
    height: 110,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "#F2F3F5",
  },
  carouselThumb: {
    width: 110,
  },
  patientName: {
    marginTop: ADMIN_TOKENS.spacing.xs,
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.xs,
    lineHeight: ADMIN_TOKENS.lineHeight.xs,
    color: ADMIN_TOKENS.color.textPrimary,
  },
  carouselImage: {
    width: "100%",
    height: "100%",
  },
  carouselImagePressable: {
    width: "100%",
    height: "100%",
  },
  imageActionRow: {
    position: "absolute",
    bottom: 4,
    right: 4,
    flexDirection: "row",
    gap: 4,
  },
  imageActionButton: {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: "transparent",
    justifyContent: "center",
    alignItems: "center",
  },
  actionIconShadow: {
    textShadow: "0px 1px 4px rgba(0,0,0,0.95)",
  } as any,
});

export default PastSimulationsSection;
